import type { Client } from '@urql/svelte'
import type { BlockInput, LocationInput } from '$lib/graphql/types'
import {
	InsertListBlockDocument,
	InsertPlaceBlockDocument,
	InsertDestinationBlockDocument,
	InsertSourceBlockDocument,
	InsertHighlightBlockDocument,
	InsertRegionBlockDocument,
	InsertCustomRegionBlockDocument,
	InsertPinBlockDocument,
	InsertDayBlockDocument,
} from '$lib/queries/generated/InsertBlock'
import { HandlePageChange } from '$lib/utils/mapRoutingState'
import { browser } from '$app/environment'
import blockState from '$lib/stores/block-state'
import { track } from '$lib/utils/track'
import { toastStore } from '$lib/stores/toast-store'
import { urlState } from '$lib/stores/url-state'
import { DeleteTripDocument } from '$lib/queries/generated/DeleteTrip'
import { PublishListingDocument } from '$lib/queries/generated/PublishListing'
import { DuplicateTripDocument } from '$lib/queries/generated/InsertTrip'
import { UnpublishListingDocument } from '$lib/queries/generated/PublishListing'
import { DeleteBlockDocument } from '$lib/queries/generated/deleteBlock'
import {
	UpdateBlockDetailsDocument,
	UpdateLocationForBlockDocument,
} from '$lib/queries/generated/UpdateBlock'
import { UpdateTripDocument } from '$lib/queries/generated/UpdateTrip'
import { get } from 'svelte/store'
import { BlockType } from '../graphql/enums'
import { QueryTripTextDocument } from '../queries/generated/QueryTripText'

type MutationResponse = { data?: any; error?: Error }

const handleOperation = async (
	client: Client,
	document: any,
	variables: any,
	options: {
		clearCustomState?: boolean
		trackEvent?: { name: string; extraData?: Record<string, string> }
		toastMessages?: {
			loading?: string
			success?: string
			error?: string
		}
		pathname?: string
	} = {},
): Promise<MutationResponse> => {
	const toastId =
		options.toastMessages?.loading &&
		toastStore.add({
			message: options.toastMessages.loading,
			type: 'loading',
		})

	try {
		// Run mutation and URL change in parallel
		const [mutationResult] = await Promise.all([
			// Handle mutation
			client.mutation(document, variables),

			// Handle URL change asynchronously
			(async () => {
				if (browser) {
					const currentUrl = options.pathname || window.location.pathname
					const { itineraryId, tripId } = get(urlState)

					blockState.clearAllCustom()

					await HandlePageChange(tripId, null, browser, currentUrl, itineraryId, null)
				}
			})(),
		])

		if (mutationResult.error) {
			throw mutationResult.error
		}

		if (toastId && options.toastMessages?.success) {
			toastStore.update(toastId, {
				message: options.toastMessages.success,
				type: 'success',
			})
		}

		if (options.trackEvent) {
			track(options.trackEvent.name, {
				tripId: variables.tripID,
				...options.trackEvent.extraData,
			})
		}

		return { data: mutationResult.data }
	} catch (error) {
		console.error('Operation failed:', error)

		if (toastId && options.toastMessages?.error) {
			toastStore.update(toastId, {
				message: options.toastMessages.error,
				type: 'error',
			})
		}

		return { error: error as Error }
	}
}

export const createMutateTripContext = (client: Client) => {
	return {
		insertListBlock: async (tripID: string, input: BlockInput) => {
			return handleOperation(
				client,
				InsertListBlockDocument,
				{ tripID, input },
				{
					trackEvent: {
						name: 'Add List Success',
						extraData: { title: input?.title ?? '' },
					},
					toastMessages: {
						loading: 'Adding list...',
						success: 'List added successfully',
						error: 'Failed to add list',
					},
				},
			)
		},

		insertPlaceBlock: async (tripID: string, input: BlockInput) => {
			return handleOperation(
				client,
				InsertPlaceBlockDocument,
				{ tripID, input },
				{
					trackEvent: {
						name: 'Add Place Success',
						extraData: { title: input?.title ?? '' },
					},
					toastMessages: {
						loading: 'Adding place...',
						success: 'Place added successfully',
						error: 'Failed to add place',
					},
				},
			)
		},

		insertDestinationBlock: async (tripID: string, input: BlockInput) => {
			return handleOperation(
				client,
				InsertDestinationBlockDocument,
				{ tripID, input },
				{
					trackEvent: {
						name: 'Add Destination Success',
						extraData: { title: input?.title ?? '' },
					},
					toastMessages: {
						loading: 'Adding destination...',
						success: 'Destination added successfully',
						error: 'Failed to add destination',
					},
				},
			)
		},

		insertSourceBlock: async (tripID: string, input: BlockInput) => {
			return handleOperation(
				client,
				InsertSourceBlockDocument,
				{ tripID, input },
				{
					trackEvent: {
						name: 'Add Source Success',
						extraData: { title: input?.title ?? '' },
					},
					toastMessages: {
						loading: 'Adding source...',
						success: 'Source added successfully',
						error: 'Failed to add source',
					},
				},
			)
		},

		insertHighlightBlock: async (tripID: string, input: BlockInput) => {
			return handleOperation(
				client,
				InsertHighlightBlockDocument,
				{ tripID, input },
				{
					trackEvent: {
						name: 'Add Highlight Success',
						extraData: { title: input?.title ?? '' },
					},
					toastMessages: {
						loading: 'Adding highlight...',
						success: 'Highlight added successfully',
						error: 'Failed to add highlight',
					},
				},
			)
		},

		insertRegionBlock: async (tripID: string, input: BlockInput) => {
			return handleOperation(
				client,
				InsertRegionBlockDocument,
				{ tripID, input },
				{
					trackEvent: {
						name: 'Add Region Success',
						extraData: { title: input?.title ?? '' },
					},
					toastMessages: {
						loading: 'Adding region...',
						success: 'Region added successfully',
						error: 'Failed to add region',
					},
				},
			)
		},

		insertCustomRegionBlock: async (tripID: string, input: BlockInput) => {
			return handleOperation(
				client,
				InsertCustomRegionBlockDocument,
				{ tripID, input },
				{
					trackEvent: {
						name: 'Add Custom Region Success',
						extraData: { title: input?.title ?? '' },
					},
					toastMessages: {
						loading: 'Adding custom region...',
						success: 'Custom region added successfully',
						error: 'Failed to add custom region',
					},
				},
			)
		},

		insertPinBlock: async (tripID: string, input: BlockInput) => {
			return handleOperation(
				client,
				InsertPinBlockDocument,
				{ tripID, input },
				{
					clearCustomState: true,
					trackEvent: {
						name: 'Add Pin Success',
						extraData: { title: input?.title ?? '' },
					},
					toastMessages: {
						loading: 'Adding pin...',
						success: 'Pin added successfully',
						error: 'Failed to add pin',
					},
				},
			)
		},

		insertDayBlock: async (tripID: string, destinationID: string, input: BlockInput) => {
			return handleOperation(
				client,
				InsertDayBlockDocument,
				{
					tripID,
					destinationID,
					input,
				},
				{
					trackEvent: {
						name: 'Add Day Success',
						extraData: {
							title: input?.title ?? '',
							destinationId: destinationID,
						},
					},
					toastMessages: {
						loading: 'Adding day...',
						success: 'Day added successfully',
						error: 'Failed to add day',
					},
				},
			)
		},

		// Add new operations
		deleteBlock: async (tripId: string, blockId: string, blockType: string) => {
			const blockTypeString = blockType === BlockType.Root ? 'Itinerary' : 'Block'
			return handleOperation(
				client,
				DeleteBlockDocument,
				{ id: blockId, blockType, tripId },
				{
					trackEvent: {
						name: `Delete ${blockTypeString}`,
						extraData: { blockId },
					},
					toastMessages: {
						loading: `Deleting ${blockTypeString}...`,
						success: `${blockTypeString} deleted successfully`,
						error: `Failed to delete ${blockTypeString}`,
					},
					pathname: `/trips/${tripId}`,
				},
			)
		},

		publishListing: async (tripID: string) => {
			return handleOperation(
				client,
				PublishListingDocument,
				{ id: tripID },
				{
					trackEvent: { name: 'Create Listing' },
					toastMessages: {
						loading: 'Creating listing...',
						success: 'Listing created successfully',
						error: 'Failed to create listing',
					},
					pathname: `/trips/${tripID}/listing`,
				},
			)
		},

		unpublishListing: async (tripID: string) => {
			return handleOperation(
				client,
				UnpublishListingDocument,
				{ tripID },
				{
					trackEvent: { name: 'Unpublish Listing' },
					toastMessages: {
						loading: 'Unpublishing listing...',
						success: 'Listing unpublished successfully',
						error: 'Failed to unpublish listing',
					},
					pathname: '/trips',
				},
			)
		},

		duplicateTrip: async (tripID: string) => {
			return handleOperation(
				client,
				DuplicateTripDocument,
				{
					id: tripID,
				},
				{
					trackEvent: { name: 'Duplicate Trip', extraData: { tripId: tripID } },
					toastMessages: {
						loading: 'Duplicating trip...',
						success: 'Trip duplicated successfully',
						error: 'Failed to duplicate trip',
					},
					pathname: '/trips',
				},
			)
		},

		deleteTrip: async (tripID: string) => {
			return handleOperation(
				client,
				DeleteTripDocument,
				{
					id: tripID,
				},
				{
					trackEvent: { name: 'Delete Trip', extraData: { tripId: tripID } },
					toastMessages: {
						loading: 'Deleting trip...',
						success: 'Trip deleted successfully',
						error: 'Failed to delete trip',
					},
					pathname: '/trips',
				},
			)
		},

		updateBlockDescription: async (blockID: string, description: string) => {
			return handleOperation(
				client,
				UpdateBlockDetailsDocument,
				{
					id: blockID,
					input: { description },
				},
				{
					trackEvent: {
						name: 'Update Description',
						extraData: { description },
					},
					toastMessages: {
						loading: 'Updating description...',
						success: 'Description updated successfully',
						error: 'Failed to update description',
					},
				},
			)
		},

		updateBlockTitle: async (blockID: string, title: string) => {
			return handleOperation(
				client,
				UpdateBlockDetailsDocument,
				{
					id: blockID,
					input: { title },
				},
				{
					trackEvent: {
						name: 'Update Title',
						extraData: { title },
					},
					toastMessages: {
						loading: 'Updating title...',
						success: 'Title updated successfully',
						error: 'Failed to update title',
					},
				},
			)
		},

		updateTripDescription: async (tripID: string, description: string) => {
			return handleOperation(
				client,
				UpdateTripDocument,
				{
					id: tripID,
					input: { description },
				},
				{
					trackEvent: {
						name: 'Update Description',
						extraData: { description },
					},
					toastMessages: {
						loading: 'Updating description...',
						success: 'Description updated successfully',
						error: 'Failed to update description',
					},
				},
			)
		},

		updateTripTitle: async (tripID: string, title: string) => {
			return handleOperation(
				client,
				UpdateTripDocument,
				{
					id: tripID,
					input: { title },
				},
				{
					trackEvent: {
						name: 'Update Title',
						extraData: { title },
					},
					toastMessages: {
						loading: 'Updating title...',
						success: 'Title updated successfully',
						error: 'Failed to update title',
					},
				},
			)
		},

		updateLocationForBlock: async (blockID: string, input: LocationInput) => {
			return handleOperation(
				client,
				UpdateLocationForBlockDocument,
				{ blockID: blockID, input },
				{
					trackEvent: {
						name: 'Update Location',
						extraData: { blockId: blockID },
					},
					toastMessages: {
						loading: 'Updating location...',
						success: 'Location updated successfully',
						error: 'Failed to update location',
					},
				},
			)
		},

		downloadTripText: async (tripID: string) => {
			const toastId = toastStore.add({
				message: 'Downloading trip text...',
				type: 'loading',
			})
			const result = await client.query(QueryTripTextDocument, { id: tripID }).toPromise()

			if (result.error) {
				toastStore.update(toastId, {
					message: 'Failed to download trip text',
					type: 'error',
				})
				return { error: result.error }
			}

			if (result.data?.tripText) {
				try {
					const blob = new Blob([result.data.tripText], { type: 'text/plain' })
					const url = window.URL.createObjectURL(blob)
					const a = document.createElement('a')
					a.href = url
					a.download = `trip-${tripID}.txt`
					document.body.appendChild(a)
					a.click()
					window.URL.revokeObjectURL(url)
					document.body.removeChild(a)

					toastStore.update(toastId, {
						message: 'Trip text downloaded successfully',
						type: 'success',
					})
					track('Download Trip Text', { tripId: tripID })
					return { data: result.data }
				} catch (error) {
					toastStore.update(toastId, {
						message: 'Failed to download trip text',
						type: 'error',
					})
					return { error: error as Error }
				}
			}

			toastStore.update(toastId, {
				message: 'No trip text available to download',
				type: 'error',
			})
			return { error: new Error('No trip text available') }
		},
	}
}

export type MutateTripContext = ReturnType<typeof createMutateTripContext>
